import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { recordingApi } from '@/services/recording'
import { useMeetingStore } from '@/stores/meeting'
import { connectSocket } from '@/services/socket'
import type { Participant } from '@/stores/participants'

/**
 * §45–49, 2026-09-14 경량 구현. 서버가 각 참가자의 WebRTC 트랙을 직접 받아 합성하는 방식
 * (docs/MEET_MEDIA_001_VALIDATION.md Option A)은 api.kei.bio가 이미 메모리 압박 상태이고
 * kei-broadcast 미디어 서버는 이 시점에 SSH가 응답하지 않아 보류 — 대신 호스트 브라우저가
 * 참가자 Stage를 <canvas>로 그려(기존 FeedRecordButton.vue의 캔버스+MediaRecorder 패턴 재사용)
 * MediaRecorder로 녹화하고, 완성된 파일을 api.kei.bio를 거치지 않고 R2에 직접 PUT 업로드한다.
 * 서버는 presigned URL 발급과 상태 부기만 담당 — 영상 바이트가 서버 메모리를 스치지 않는다.
 *
 * 알려진 한계(§45 원안과의 명시적 차이, 사용자 승인 후 진행):
 * - "공식 녹화"가 호스트 한 명의 브라우저 렌더링 결과물이다 — 호스트가 탭을 닫거나 연결이
 *   끊기면 녹화가 중단된다(서버 다중 소스 합성이 아님).
 * - Master composite(전체 화면 합성본)만 가능 — §46-B의 참가자별 ISO 트랙 저장은 이 방식으로는
 *   불가능(캔버스에 이미 합쳐진 화면만 남음).
 * - 화면공유/프레젠테이션 오버레이는 V1 범위 밖 — 항상 참가자 카메라 그리드만 녹화한다.
 */

const error = ref<string | null>(null)
// 호스트/관리자의 "공식 녹화"(meeting.recordingState, 서버+R2)와 별개로, 일반 참가자가 자기
// 기기에만 저장하는 로컬 녹화는 서버에 전혀 알리지 않는 순수 클라이언트 상태다.
const localRecordingActive = ref(false)
// 2026-09-14 — "R2 업로드 도중 회의를 종료하거나 탭을 닫으면 녹화가 통째로 날아간다" 실사용 지적
// 대응. R2 PUT은 host의 업로드 대역폭을 실제 통화와 나눠 쓰므로(같은 네트워크 경로), 업로드가
// 끝나기 전에 방을 나가면 fetch 자체가 끊겨 녹화가 유실된다. App.vue의 전역 배너/beforeunload
// 가드가 이 값을 보고 "업로드 중" 안내를 띄우고, 탭을 닫으려 하면 막는다 — ref라 어디서든(App.vue
// 포함) 반응형으로 구독 가능.
const uploadInProgress = ref(false)
let pendingUpload: Promise<void> | null = null
// 2026-09-14 — "업로드 진행률 표시(38% · 124MB/326MB)" + "실패해도 OPFS 파일 지우지 말고
// 재전송 버튼" 지시 대응. fetch()는 업로드 진행률 이벤트가 없어(다운로드만 스트림 가능)
// XMLHttpRequest의 upload.onprogress로 바꿔야 실제 %/바이트를 얻을 수 있다.
const uploadProgress = ref(0) // 0-100
const uploadedBytes = ref(0)
const totalBytes = ref(0)
const uploadFailed = ref(false)
let lastFailedUpload: { meetingId: string; blob: Blob; ext: 'mp4' | 'webm' } | null = null
// 2026-09-14 — "기기가 버벅이면 녹화 중단을 안내할 수 있는지" 요청. 우리 자신의 rAF 루프
// (drawFrame)가 목표 주기(30fps≈33ms)보다 눈에 띄게 느려지는 걸 곧 메인스레드 과부하 신호로
// 본다 — 별도 성능 API 없이도 "우리가 직접 바쁘게 돌리는 루프가 느려졌다"는 가장 직접적인 증거.
const performanceWarning = ref(false)
let frameTimestamps: number[] = []
const FRAME_TARGET_MS = 1000 / 30
const OVERLOAD_THRESHOLD_MS = FRAME_TARGET_MS * 2.5 // 평균 프레임 간격이 목표의 2.5배 이상
const OVERLOAD_SAMPLE_WINDOW = 90 // 최근 ~3초치(30fps 기준) 프레임 간격을 본다
let mediaRecorder: MediaRecorder | null = null
let chunks: Blob[] = [] // OPFS 미지원 브라우저(구형 Safari/일부 WebView)를 위한 폴백 전용
let recordedMimeType = ''
let usingOpfs = false // 이번 녹화 세션이 실제로 OPFS를 썼는지 — beginCapture마다 새로 결정됨

/**
 * 2026-09-14 사용자 지시 — "브라우저 메모리에만 들고 있으면 안 된다. 1시간 회의를 전부 RAM에
 * 들고 있으면 모바일에서 위험하다." 기존엔 MediaRecorder의 모든 청크를 `chunks[]` 배열(JS 힙)에
 * 계속 쌓아뒀다 — 2.5Mbps 비디오 기준 1시간이면 ~1.1GB를 JS 참조로 계속 들고 있는 셈이라, iOS
 * Safari 등 탭당 메모리 한도가 빡빡한 환경에서 긴 회의 중 탭이 죽을 위험이 실제로 있었다.
 * 지원 브라우저에서는 청크를 받는 즉시 OPFS(Origin Private File System, 디스크 기반) 임시
 * 파일에 순서대로 써서 JS 참조를 바로 놓아준다 — 녹화가 끝난 뒤에야 OPFS의 File(디스크 기반,
 * 업로드 시 스트리밍되고 한 번에 메모리에 안 올라감)을 그대로 R2 PUT/로컬 다운로드에 사용한다.
 * OPFS를 지원 안 하는 환경만 예전처럼 메모리 배열로 폴백.
 */
const opfsSupported = typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function'
let opfsFileHandle: FileSystemFileHandle | null = null
let opfsWritable: FileSystemWritableFileStream | null = null
let opfsFileName = ''
let opfsWriteQueue: Promise<void> = Promise.resolve() // ondataavailable이 write() 완료보다 먼저 여러 번 불릴 수 있어 순서 보장 필요

async function openOpfsFile(ext: string): Promise<boolean> {
  try {
    const root = await navigator.storage.getDirectory()
    opfsFileName = `meet-recording-${Date.now()}.${ext}`
    opfsFileHandle = await root.getFileHandle(opfsFileName, { create: true })
    opfsWritable = await opfsFileHandle.createWritable()
    return true
  } catch (e) {
    console.warn('[useRecording] OPFS 파일 열기 실패 — 메모리 폴백으로 전환', e)
    opfsFileHandle = null
    opfsWritable = null
    opfsFileName = ''
    return false
  }
}

function queueOpfsWrite(blob: Blob) {
  opfsWriteQueue = opfsWriteQueue.then(async () => {
    if (opfsWritable) await opfsWritable.write(blob)
  }).catch((e) => console.error('[useRecording] OPFS write 실패', e))
}

async function closeOpfsFile(): Promise<File | null> {
  await opfsWriteQueue
  if (!opfsWritable || !opfsFileHandle) return null
  try {
    await opfsWritable.close()
    return await opfsFileHandle.getFile() // 디스크 기반 File — 업로드/다운로드가 이걸 그대로 스트리밍해서 씀
  } catch (e) {
    console.error('[useRecording] OPFS 파일 닫기 실패', e)
    return null
  }
}

// 업로드(또는 로컬 저장) 완료 후 임시 파일 정리 — 사용자 지시: "완료 후 로컬 임시파일 삭제".
async function deleteOpfsFile() {
  const name = opfsFileName
  opfsFileHandle = null
  opfsWritable = null
  opfsFileName = ''
  if (!name) return
  try {
    const root = await navigator.storage.getDirectory()
    await root.removeEntry(name)
  } catch (e) {
    console.warn('[useRecording] OPFS 임시파일 삭제 실패(무시 가능)', e)
  }
}

let canvas: HTMLCanvasElement | null = null
let ctx: CanvasRenderingContext2D | null = null
let rafId: number | null = null
let audioCtx: AudioContext | null = null
let audioDest: MediaStreamAudioDestinationNode | null = null
const hiddenVideoEls = new Map<string, HTMLVideoElement>()
const connectedAudioStreamIds = new Set<string>()

// MeetRoom.vue가 매 렌더마다 최신값으로 갱신 — 녹화 루프는 이 모듈 레벨 변수를 읽어서 그 순간의
// 실제 참가자/스트림을 그린다(참가자 입장/퇴장/카메라 on-off가 녹화 중에도 그대로 반영되도록).
let liveParticipants: Participant[] = []
let liveStreams: Record<string, MediaStream | null> = {}
let boundMeetingId: string | null = null

const CANVAS_WIDTH = 1280
const CANVAS_HEIGHT = 720

function ensureHiddenVideo(userId: string, stream: MediaStream): HTMLVideoElement {
  let el = hiddenVideoEls.get(userId)
  if (!el) {
    el = document.createElement('video')
    el.muted = true // 캔버스에 그릴 프레임 소스로만 쓴다 — 실제 소리는 Web Audio 믹싱 쪽에서 따로 가져간다.
    el.playsInline = true
    hiddenVideoEls.set(userId, el)
  }
  if (el.srcObject !== stream) {
    el.srcObject = stream
    el.play().catch(() => {})
  }
  return el
}

function pruneHiddenVideos(activeUserIds: Set<string>) {
  for (const [userId, el] of hiddenVideoEls) {
    if (!activeUserIds.has(userId)) {
      el.srcObject = null
      hiddenVideoEls.delete(userId)
    }
  }
}

// §26 "4인 Layout"과 같은 기본 그리드 규칙(스포트라이트 등 상호작용 레이아웃은 녹화에선 재현하지
// 않는다 — 누가 화면에서 컸는지는 보는 사람마다 다르게 조작할 수 있는 UI 상태라 객관적인 기록에는
// 모두 같은 크기의 그리드가 더 적합하다는 판단).
function gridCells(n: number): { cols: number; rows: number } {
  if (n <= 1) return { cols: 1, rows: 1 }
  if (n === 2) return { cols: 2, rows: 1 }
  if (n === 3) return { cols: 2, rows: 2 }
  return { cols: 2, rows: 2 }
}

function recordFrameTiming() {
  frameTimestamps.push(performance.now())
  if (frameTimestamps.length > OVERLOAD_SAMPLE_WINDOW + 1) frameTimestamps.shift()
  if (frameTimestamps.length < OVERLOAD_SAMPLE_WINDOW) return

  const deltas: number[] = []
  for (let i = 1; i < frameTimestamps.length; i++) deltas.push(frameTimestamps[i] - frameTimestamps[i - 1])
  const avg = deltas.reduce((a, b) => a + b, 0) / deltas.length
  performanceWarning.value = avg > OVERLOAD_THRESHOLD_MS
}

function drawFrame() {
  if (!ctx || !canvas) return
  recordFrameTiming()
  ctx.fillStyle = '#0b0b0c'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const activeUserIds = new Set<string>()
  const cells: { userId: string; name: string; video: HTMLVideoElement | null }[] = []
  for (const p of liveParticipants) {
    const stream = liveStreams[p.userId]
    if (!stream) continue
    activeUserIds.add(p.userId)
    const hasVideo = stream.getVideoTracks().some((t) => t.readyState === 'live')
    cells.push({ userId: p.userId, name: p.displayName, video: hasVideo ? ensureHiddenVideo(p.userId, stream) : null })
  }
  pruneHiddenVideos(activeUserIds)

  const { cols, rows } = gridCells(cells.length || 1)
  const cellW = canvas.width / cols
  const cellH = canvas.height / rows

  cells.forEach((cell, i) => {
    const col = i % cols
    const row = Math.floor(i / cols)
    const x = col * cellW
    const y = row * cellH

    if (cell.video && cell.video.readyState >= 2) {
      const vw = cell.video.videoWidth || 16
      const vh = cell.video.videoHeight || 9
      const scale = Math.min(cellW / vw, cellH / vh)
      const dw = vw * scale
      const dh = vh * scale
      ctx!.fillStyle = '#16161a'
      ctx!.fillRect(x, y, cellW, cellH)
      ctx!.drawImage(cell.video, x + (cellW - dw) / 2, y + (cellH - dh) / 2, dw, dh)
    } else {
      ctx!.fillStyle = '#16161a'
      ctx!.fillRect(x, y, cellW, cellH)
    }

    // 참가자 이름 라벨 — ParticipantTile.vue의 tile__label과 같은 목적(누가 누군지 기록에 남게).
    ctx!.font = '600 20px -apple-system, "Segoe UI", sans-serif'
    const label = cell.name
    const metrics = ctx!.measureText(label)
    const padX = 10
    const labelH = 30
    ctx!.fillStyle = 'rgba(0,0,0,0.55)'
    ctx!.fillRect(x + 12, y + cellH - labelH - 12, metrics.width + padX * 2, labelH)
    ctx!.fillStyle = '#fff'
    ctx!.fillText(label, x + 12 + padX, y + cellH - 12 - 9)
  })

  rafId = requestAnimationFrame(drawFrame)
}

function connectAudio(stream: MediaStream) {
  if (!audioCtx || !audioDest) return
  if (connectedAudioStreamIds.has(stream.id)) return
  if (!stream.getAudioTracks().some((t) => t.readyState === 'live')) return
  try {
    const source = audioCtx.createMediaStreamSource(stream)
    const gain = audioCtx.createGain()
    gain.gain.value = 1.0
    source.connect(gain).connect(audioDest)
    connectedAudioStreamIds.add(stream.id)
  } catch {
    // 트랙이 아직 준비 안 된 경우 등 — 다음 tick에서 다시 시도됨(updateSources가 계속 불림).
  }
}

// 실제 녹화 가능 여부와 무관하게 표시용 mp4/webm 확장자 판별 — FeedRecordButton.vue와 동일 로직
// (사파리는 h264+aac mp4를 직접 지원, 크롬 계열은 webm).
function verifyMp4H264Aac(): string | null {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null
  if (!MediaRecorder.isTypeSupported('video/mp4;codecs=h264,aac')) return null
  try {
    const probe = new MediaRecorder(new MediaStream(), { mimeType: 'video/mp4;codecs=h264,aac' })
    const mt = (probe.mimeType || '').toLowerCase()
    return mt.includes('mp4') && !mt.includes('opus') ? probe.mimeType : null
  } catch {
    return null
  }
}
const WEBM_CANDIDATES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null
  const verifiedMp4 = verifyMp4H264Aac()
  if (verifiedMp4) return verifiedMp4
  return WEBM_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || null
}

function teardown() {
  if (rafId !== null) cancelAnimationFrame(rafId)
  rafId = null
  for (const el of hiddenVideoEls.values()) el.srcObject = null
  hiddenVideoEls.clear()
  connectedAudioStreamIds.clear()
  audioCtx?.close().catch(() => {})
  audioCtx = null
  audioDest = null
  canvas = null
  ctx = null
  mediaRecorder = null
  chunks = []
  frameTimestamps = []
  performanceWarning.value = false
}

// canvas/오디오믹싱/MediaRecorder 준비 — 호스트의 "공식 녹화"와 일반 참가자의 "내 기기에 저장"
// 둘 다 캡처 방식 자체는 동일하고, 끝난 뒤 파일을 어디로 보내는지(R2 업로드 vs 로컬 다운로드)만
// 다르다(2026-09-14, 사용자 지시 — 전체화면 녹화 R2 업로드는 서버 부담이 있으니 관리자/호스트만).
async function beginCapture(mimeType: string): Promise<boolean> {
  const ext = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
  usingOpfs = opfsSupported && (await openOpfsFile(ext))

  canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH
  canvas.height = CANVAS_HEIGHT
  ctx = canvas.getContext('2d')
  audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
  audioDest = audioCtx.createMediaStreamDestination()
  for (const p of liveParticipants) {
    const s = liveStreams[p.userId]
    if (s) connectAudio(s)
  }

  drawFrame()
  // 첫 프레임이 실제로 페인트될 시간(두 번의 rAF)을 기다린 뒤에야 captureStream/recorder.start를
  // 부른다 — FeedRecordButton.vue와 동일 이유(시작 직후의 빈/검은 프레임 방지).
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))
  if (!canvas || !audioDest) return false // 그 사이 stop()이 먼저 불려 teardown된 경우

  const videoTrack = canvas.captureStream(30).getVideoTracks()[0]
  const combined = new MediaStream([videoTrack, ...audioDest.stream.getAudioTracks()])

  recordedMimeType = mimeType
  chunks = []
  try {
    mediaRecorder = new MediaRecorder(combined, {
      mimeType,
      videoBitsPerSecond: 2_500_000,
      audioBitsPerSecond: 128_000
    })
  } catch {
    teardown()
    return false
  }
  mediaRecorder.ondataavailable = (e) => {
    if (!e.data.size) return
    if (usingOpfs) queueOpfsWrite(e.data)
    else chunks.push(e.data)
  }
  mediaRecorder.start(1000) // 1초 단위 타임슬라이스 — 중간에 브라우저가 죽어도 청크가 남아있게.
  return true
}

async function endCapture(): Promise<Blob | null> {
  if (!mediaRecorder || mediaRecorder.state === 'inactive') return null
  const recorder = mediaRecorder
  const mimeType = recordedMimeType
  const wasUsingOpfs = usingOpfs
  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve()
  })
  if (rafId !== null) cancelAnimationFrame(rafId)
  rafId = null
  recorder.stop()
  await stopped
  // OPFS를 썼으면 디스크 파일(File, Blob의 하위타입)을 그대로 쓴다 — write 큐가 이미 순서대로
  // 디스크에 쓴 것을 닫아서 돌려줄 뿐, 여기서 다시 메모리에 모으지 않는다. 임시파일 자체는
  // 업로드/로컬저장이 끝날 때까지 지우지 않는다(재전송 대비).
  const blob = wasUsingOpfs ? await closeOpfsFile() : new Blob(chunks, { type: mimeType })
  teardown()
  return blob && blob.size ? blob : null
}

// FeedRecordButton.vue와 동일한 로컬 다운로드 트리거 — 참가자 로컬 녹화는 서버를 전혀 거치지
// 않고 그 자리에서 자기 기기(PC/스마트폰)에 파일로 저장된다.
function downloadBlob(blob: Blob, ext: string, roomCode: string) {
  const url = URL.createObjectURL(blob)
  const ts = new Date().toISOString().replace(/[:.]/g, '-')
  const a = document.createElement('a')
  a.href = url
  a.download = `meet-${roomCode || 'recording'}-${ts}.${ext}`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

// fetch()는 업로드 바이트 진행률을 못 준다(다운로드 스트림만 가능) — XHR의 upload.onprogress로
// 실제 "%· 전송량/전체용량"을 얻는다. R2 presigned PUT은 표준 HTTP PUT이라 그대로 호환.
function putWithProgress(url: string, blob: Blob, contentType: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return
      uploadedBytes.value = e.loaded
      totalBytes.value = e.total
      uploadProgress.value = Math.round((e.loaded / e.total) * 100)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`R2_PUT_${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('R2_PUT_NETWORK_ERROR'))
    xhr.send(blob)
  })
}

export function useRecording() {
  const { t } = useI18n()
  const meeting = useMeetingStore()

  // MeetRoom.vue가 streams/participants가 바뀔 때마다 호출 — 녹화 루프가 최신 그리드를 그리고,
  // 새로 들어온 참가자의 오디오도 자동으로 믹싱 그래프에 연결된다.
  function updateSources(participants: Participant[], streams: Record<string, MediaStream | null>) {
    liveParticipants = participants
    liveStreams = streams
    if (audioCtx && audioDest) {
      for (const p of participants) {
        const s = streams[p.userId]
        if (s) connectAudio(s)
      }
    }
  }

  function bindStateSync(meetingId: string) {
    if (boundMeetingId === meetingId) return
    boundMeetingId = meetingId
    const socket = connectSocket()
    // 호스트가 아닌 참가자는 직접 캡처를 돌리지 않으므로, 서버가 브로드캐스트하는 상태로만
    // "● REC" 표시를 맞춘다(§48 — 녹화 사실은 참가자 전원에게 항상 보여야 함).
    socket.on('meet:recording-state', (payload: { meetingId: string; state: typeof meeting.recordingState; masterUrl: string | null }) => {
      if (payload.meetingId !== meetingId) return
      meeting.recordingState = payload.state
      meeting.recordingUrl = payload.masterUrl
    })
  }

  function unbindStateSync() {
    const socket = connectSocket()
    socket.off('meet:recording-state')
    boundMeetingId = null
  }

  // isHost=true: 공식 녹화(서버 상태 부기 + R2 업로드, §48 "● REC" 전원 노출).
  // isHost=false: 참가자 로컬 녹화 — 서버 호출 자체가 없다(2026-09-14 지시: R2 업로드는 지금은
  // 호스트/관리자 전용, 참가자는 자기 PC·스마트폰에 저장하는 방식만 제공).
  async function start(meetingId: string, isHost: boolean) {
    error.value = null
    const mimeType = pickMimeType()
    if (!mimeType) {
      error.value = t('errors.recordingStartFailed')
      return
    }

    if (isHost) {
      try {
        await recordingApi.start(meetingId)
      } catch {
        meeting.recordingState = 'FAILED'
        error.value = t('errors.recordingStartFailed')
        return
      }
    }

    const ok = await beginCapture(mimeType)

    if (!ok) {
      error.value = t('errors.recordingStartFailed')
      if (isHost) {
        meeting.recordingState = 'FAILED'
        await recordingApi.fail(meetingId).catch(() => {})
      }
      return
    }

    if (isHost) meeting.recordingState = 'RECORDING'
    else localRecordingActive.value = true
  }

  // recordingApi.stop() + upload-url 발급 + R2 PUT + complete까지 한 묶음 — 통화 중 즉시
  // 업로드(stop)와 "종료 후 백그라운드 업로드"(stopAndUploadInBackground) 둘 다 이걸 공유한다.
  function runUpload(meetingId: string, blob: Blob, ext: 'mp4' | 'webm'): Promise<void> {
    uploadInProgress.value = true
    uploadFailed.value = false
    uploadProgress.value = 0
    uploadedBytes.value = 0
    totalBytes.value = blob.size
    const uploadPromise = (async () => {
      try {
        await recordingApi.stop(meetingId)
        const { uploadUrl, fileUrl, contentType } = await recordingApi.getUploadUrl(meetingId, ext)
        await putWithProgress(uploadUrl, blob, contentType)
        await recordingApi.complete(meetingId, fileUrl)
        meeting.recordingState = 'READY'
        meeting.recordingUrl = fileUrl
        uploadProgress.value = 100
        lastFailedUpload = null
        await deleteOpfsFile() // 성공했을 때만 임시파일 삭제 — 실패 시엔 재전송을 위해 남겨둔다.
      } catch {
        meeting.recordingState = 'FAILED'
        error.value = t('errors.recordingStopFailed')
        uploadFailed.value = true
        lastFailedUpload = { meetingId, blob, ext } // OPFS 임시파일은 지우지 않는다 — "다시 전송" 버튼용.
        await recordingApi.fail(meetingId).catch(() => {})
      } finally {
        pendingUpload = null
        uploadInProgress.value = false
      }
    })()
    pendingUpload = uploadPromise
    return uploadPromise
  }

  // 업로드가 R2 PUT 도중 끊겨도(네트워크 끊김 등) OPFS 임시파일은 살아있으므로, 같은 blob으로
  // 그대로 재시도할 수 있다 — 녹화를 다시 하지 않아도 된다.
  function retryUpload(): Promise<void> | null {
    if (!lastFailedUpload) return null
    const { meetingId, blob, ext } = lastFailedUpload
    return runUpload(meetingId, blob, ext)
  }

  function hasRetryableUpload(): boolean {
    return !!lastFailedUpload
  }

  // 통화를 계속 이어가면서 "녹화만" 끄는 일반 경로 — 업로드가 끝날 때까지 이 함수 호출자가 기다린다
  // (RecordingSheet.vue의 "녹화 종료" 버튼).
  async function stop(meetingId: string, isHost: boolean) {
    const blob = await endCapture()

    if (!blob) {
      if (isHost) {
        meeting.recordingState = 'FAILED'
        await recordingApi.fail(meetingId).catch(() => {})
      } else {
        localRecordingActive.value = false
      }
      error.value = t('errors.recordingStopFailed')
      return
    }

    const ext = recordedMimeType.startsWith('video/mp4') ? 'mp4' : 'webm'

    if (!isHost) {
      // 서버를 전혀 거치지 않고 바로 기기에 저장 — R2 업로드 없음.
      downloadBlob(blob, ext, meeting.roomCode)
      await deleteOpfsFile() // 업로드가 뒤따르지 않으므로 임시파일을 바로 정리한다.
      localRecordingActive.value = false
      return
    }

    meeting.recordingState = 'PROCESSING'
    await runUpload(meetingId, blob, ext)
  }

  /**
   * 2026-09-14 사용자 지시 — "종료 버튼을 누르면 회의는 바로 종료되고, 그 다음에 R2 업로드를
   * 시작하고, 유튜브처럼 현재 창을 유지하라고 안내". 통화가 끝난 뒤에 업로드하면 그 시점부턴
   * WebRTC가 더 이상 업로드 대역폭을 나눠 쓰지 않으니(통화 자체가 끝났으므로) 화질 저하 걱정이
   * 없다. 또한 "R2 올리기 전에 내 기기에 먼저 저장"을 우선 — 업로드가 실패하거나 사용자가
   * 성급하게 탭을 닫아도 최소한 로컬 파일은 항상 남는다. 이 함수는 로컬 저장까지만 기다리고
   * 반환한다 — R2 업로드 자체는 백그라운드에서 계속되고(uploadInProgress/pendingUpload로 추적),
   * 호출부(MeetRoom.vue의 confirmEnd)는 곧장 회의 종료·페이지 이동을 진행해도 된다.
   */
  async function stopAndUploadInBackground(meetingId: string): Promise<void> {
    const blob = await endCapture()
    if (!blob) {
      meeting.recordingState = 'FAILED'
      error.value = t('errors.recordingStopFailed')
      await recordingApi.fail(meetingId).catch(() => {})
      return
    }
    const ext = recordedMimeType.startsWith('video/mp4') ? 'mp4' : 'webm'
    downloadBlob(blob, ext, meeting.roomCode) // 안전망 — R2 업로드 성패와 무관하게 항상 먼저 저장.
    meeting.recordingState = 'PROCESSING'
    void runUpload(meetingId, blob, ext) // 의도적으로 await 안 함 — 백그라운드로 흘려보낸다.
  }

  // 업로드가 진행 중이면 그 Promise를, 아니면 즉시 resolve — beforeunload 가드가 이걸 await해서
  // R2 PUT이 끝나기 전에는 탭을 못 닫게 막는다.
  function waitForPendingUpload(): Promise<void> {
    return pendingUpload || Promise.resolve()
  }

  function isUploadInProgress(): boolean {
    return uploadInProgress.value
  }

  // recordingState/recordingUrl은 여기서 값을 복사해 내려주지 않는다 — Pinia state 프로퍼티를
  // 그대로 구조분해하면 호출 시점 스냅샷이 되어 이후 변화가 반영되지 않는다(반응성 끊김 버그).
  // 호출부는 useMeetingStore()를 직접 읽어 meeting.recordingState/recordingUrl을 반응형으로 쓴다.
  // error/localRecordingActive/uploadInProgress/performanceWarning은 ref 자체를 그대로 돌려주므로
  // (구조분해로 값만 복사하는 게 아니라 .value 접근이 유지되므로) 반응성이 끊기지 않는다.
  return {
    error,
    localRecordingActive,
    uploadInProgress,
    performanceWarning,
    uploadProgress,
    uploadedBytes,
    totalBytes,
    uploadFailed,
    start,
    stop,
    stopAndUploadInBackground,
    updateSources,
    bindStateSync,
    unbindStateSync,
    waitForPendingUpload,
    isUploadInProgress,
    retryUpload,
    hasRetryableUpload
  }
}
